import {Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {RoleAssignApiService} from '../role-assign/role-assign-api.service';
import {RoleAssignmentAction, RoleAssignmentDto} from '../../../models/generated-models';
import {ConfirmDialogService} from '../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {ToastService} from '../../shared/commons/toast/toast-service';
import {RoleInvitationService} from './role-invitation.service';
import {PageTitleComponent} from '../../shared/commons/page-title/page-title.component';
import {roleAssignmentActionLabel} from '../../app.constants';
import {AuthService} from '../../shared/services/auth.service';
import {CurrentUserContextService} from '../../shared/services/current-user-context.service';
import {switchMap} from 'rxjs/operators';

@Component({
  selector: 'app-role-invitation-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, PageTitleComponent],
  templateUrl: './role-invitation-detail.component.html',
  styleUrl: './role-invitation-detail.component.scss'
})
export class RoleInvitationDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(RoleAssignApiService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  private readonly invitationService = inject(RoleInvitationService);
  private readonly authService = inject(AuthService);
  private readonly currentUserContext = inject(CurrentUserContextService);

  protected readonly loading = signal(true);
  protected readonly processing = signal(false);
  protected readonly item = signal<RoleAssignmentDto | null>(null);

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (!idParam) {
      void this.router.navigate(['/profile/role-invitations']);
      return;
    }
    const id = Number(idParam);
    if (isNaN(id)) {
      void this.router.navigate(['/profile/role-invitations']);
      return;
    }
    this.loadDetail(id);
  }

  loadDetail(id: number): void {
    this.loading.set(true);
    this.api.get(id).subscribe({
      next: (data) => {
        this.item.set(data);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toast.error('ไม่พบข้อมูลคำเชิญ หรือคุณไม่มีสิทธิ์เข้าถึง');
        void this.router.navigate(['/profile/role-invitations']);
      }
    });
  }

  formatDate(dateVal: any): string {
    if (!dateVal) return '-';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return '-';
      return new Intl.DateTimeFormat('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(d);
    } catch {
      return '-';
    }
  }

  formatDateOnly(dateVal: any): string {
    if (!dateVal) return '-';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return '-';
      return new Intl.DateTimeFormat('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(d);
    } catch {
      return '-';
    }
  }

  getStatusLabel(status?: string): string {
    switch (status?.toUpperCase()) {
      case 'ACCEPTED':
        return 'ตอบรับแล้ว';
      case 'REJECTED':
        return 'ปฏิเสธแล้ว';
      case 'PENDING':
      default:
        return 'รอการตอบรับ';
    }
  }

  getStatusClass(status?: string): string {
    switch (status?.toUpperCase()) {
      case 'ACCEPTED':
        return 'status-approved';
      case 'REJECTED':
        return 'status-rejected';
      case 'PENDING':
      default:
        return 'status-pending';
    }
  }

  getActionLabel(action?: RoleAssignmentAction | string): string {
    return roleAssignmentActionLabel(action as RoleAssignmentAction);
  }

  onAccept(): void {
    const current = this.item();
    if (!current?.id) return;

    this.confirmDialog.open({
      title: 'ยืนยันการตอบรับคำเชิญ',
      content: `<div class="text-center">คุณต้องการตอบรับการมอบสิทธิ์เพื่อดำเนินการแทนจาก <br/>  "${current.grantorName || 'ผู้มอบสิทธิ์'}" ใช่หรือไม่?</div>`,
      isInnerHTML: true,
      confirmButtonMsg: 'ยืนยันตอบรับ',
      closeButtonMsg: 'ยกเลิก'
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.processing.set(true);
      this.api.accept(current.id!).subscribe({
        next: (updated) => {
          this.processing.set(false);
          this.item.set(updated);
          this.toast.success('ตอบรับคำเชิญเรียบร้อยแล้ว');
          this.invitationService.refreshPendingCount().subscribe({
            error: () => {
            }
          });
          // Accepting made the user a ตัวแทนดำเนินการ (work item 173): pull a fresh token and
          // profile so the new role — and the account switcher — show without a re-login.
          this.authService.refreshSessionAndProfile().pipe(
            switchMap(() => this.currentUserContext.refresh())
          ).subscribe({
            error: () => {
            }
          });
        },
        error: (err) => {
          this.processing.set(false);
          this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาดในการตอบรับคำเชิญ');
        }
      });
    });
  }

  onReject(): void {
    const current = this.item();
    if (!current?.id) return;

    this.confirmDialog.open({
      title: 'ยืนยันการปฏิเสธคำเชิญ',
      content: `คุณต้องการปฏิเสธการมอบสิทธิ์จาก "${current.grantorName || 'ผู้มอบสิทธิ์'}" ใช่หรือไม่? เมื่อปฏิเสธแล้วจะไม่สามารถกู้คืนสิทธิ์นี้ได้`,
      confirmButtonMsg: 'ยืนยันปฏิเสธ',
      closeButtonMsg: 'ยกเลิก'
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.processing.set(true);
      this.api.reject(current.id!).subscribe({
        next: (updated) => {
          this.processing.set(false);
          this.item.set(updated);
          this.toast.success('ปฏิเสธคำเชิญเรียบร้อยแล้ว');
          this.invitationService.refreshPendingCount().subscribe({
            error: () => {
            }
          });
        },
        error: (err) => {
          this.processing.set(false);
          this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาดในการปฏิเสธคำเชิญ');
        }
      });
    });
  }
}
